import {
  doc, getDoc, setDoc, collection, getDocs, addDoc,
  updateDoc, deleteDoc, query, orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';

// ── Organisation Configuration ──────────────────────────────────────────────

export const getOrgConfig = async (orgId) => {
  if (!orgId) throw new Error('Organisation ID is required');
  const orgRef = doc(db, 'organizations', orgId);
  const orgDoc = await getDoc(orgRef);
  if (orgDoc.exists()) {
    const data = orgDoc.data();
    return {
      orgId: data.orgId || orgId,
      branding: data.branding || {},
      contact: data.contact || { address: '', phone: '', email: '', website: '', socialMedia: {} },
      aboutUs: data.aboutUs || { mission: '', vision: '', description: '', yearFounded: '', registrationNumber: '' },
      officeHours: data.officeHours || null,
      enabledModules: data.enabledModules || null,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      status: data.status || 'active'
    };
  }
  return null;
};

export const updateOrgConfig = async (orgId, configData) => {
  if (!orgId) throw new Error('Organisation ID is required');
  const orgRef = doc(db, 'organizations', orgId);
  // eslint-disable-next-line no-unused-vars
  const { createdAt, updatedAt, ...dataToSave } = configData;
  await setDoc(orgRef, {
    ...dataToSave,
    orgId,
    updatedAt: serverTimestamp()
  }, { merge: true });
  return true;
};

export const createOrganisation = async (orgId, initialData) => {
  const orgRef = doc(db, 'organizations', orgId);
  await setDoc(orgRef, {
    orgId,
    ...initialData,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return true;
};

// ── Members ───────────────────────────────────────────────────────────────────

export const getMembers = async (orgId) => {
  const membersRef = collection(db, 'organizations', orgId, 'members');
  const snapshot = await getDocs(query(membersRef, orderBy('createdAt', 'desc')));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addMember = async (orgId, memberData) => {
  const membersRef = collection(db, 'organizations', orgId, 'members');
  const docRef = await addDoc(membersRef, { ...memberData, createdAt: serverTimestamp() });
  return docRef.id;
};

export const updateMember = async (orgId, memberId, memberData) => {
  const memberRef = doc(db, 'organizations', orgId, 'members', memberId);
  await updateDoc(memberRef, { ...memberData, updatedAt: serverTimestamp() });
  return true;
};

export const deleteMember = async (orgId, memberId) => {
  await deleteDoc(doc(db, 'organizations', orgId, 'members', memberId));
  return true;
};

// ── Events (legacy helpers — delegate to root-level eventsApi) ───────────────
// NOTE: All event operations now use root-level collections (events, registrations,
// media, volunteer_signups). These thin wrappers are kept for backwards compatibility
// in case anything still imports from orgApi. Prefer importing directly from eventsApi.

export const getEvents = async (orgId) => {
  const { getEvents: _getEvents } = await import('./eventsApi');
  return _getEvents(orgId);
};

export const addEvent = async (orgId, eventData, createdBy = 'admin') => {
  const { createEvent } = await import('./eventsApi');
  return createEvent(orgId, eventData, createdBy);
};

export const updateEvent = async (orgId, eventId, eventData) => {
  const { updateEvent: _updateEvent } = await import('./eventsApi');
  await _updateEvent(orgId, eventId, eventData);
  return true;
};

export const deleteEvent = async (orgId, eventId) => {
  await deleteDoc(doc(db, 'events', eventId));
  return true;
};

// ── Volunteers ─────────────────────────────────────────────────────────────────

export const getVolunteers = async (orgId) => {
  const volunteersRef = collection(db, 'organizations', orgId, 'volunteers');
  const snapshot = await getDocs(volunteersRef);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};
